import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router'
import { api, ErroreApi } from '../../servizi/api.ts'
import { inAppNativa, telefonoCorrente, togliNotificheConsegnate } from '../../servizi/app-nativa.ts'
import { Bottone, Etichetta, Riquadro, Tabella, totaleDi, type ConTotale } from '../../componenti/base.tsx'
import { Avviso, Vuoto } from '../../componenti/Pezzi.tsx'
import { giorno, Guscio, LIMITE, messaggioErrore } from '../../componenti/elenco.tsx'
import { collegamentoNotifica } from '../../servizi/notifiche.ts'
import { PreferenzeAvvisi } from '../../componenti/PreferenzeAvvisi.tsx'

export function Notifiche() {
  type Riga = {
    id: string; titolo: string; corpo: string | null; creata_il: string
    letta_il: string | null; priorita: string
    oggetto_tipo: string | null; oggetto_id: string | null; persona_id: string | null
  }
  // La rotta /notifiche restituisce un oggetto con il conteggio da leggere, non un array:
  // la finestra si costruisce qui invece di passare per usaElenco.
  const [dati, setDati] = useState<(Riga & ConTotale)[] | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [salta, setSalta] = useState(0)

  const carica = useCallback(() => {
    api.get<{ notifiche: (Riga & ConTotale)[] }>(`/notifiche?limite=${LIMITE}&salta=${salta}`)
      .then((r) => setDati(r.notifiche))
      .catch((e) => setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.'))
  }, [salta])
  useEffect(carica, [carica])

  async function segnaLette() { await api.post('/notifiche/lette', {}); carica() }

  // Nell'app, aperta la pagina degli avvisi le notifiche sullo schermo del telefono non servono piu'.
  useEffect(() => { void togliNotificheConsegnate() }, [])

  return (
    <>
      <Guscio titolo="Notifiche" errore={errore} dati={dati} vuoto="Nessuna notifica."
              finestra={{ salta, totale: totaleDi(dati), cambia: setSalta }}
              intestazioni={['Quando', 'Notifica', 'Stato']}
              azioni={<Bottone variante="secondario" onClick={() => void segnaLette()}>
                        Segna tutte come lette
                      </Bottone>}>
        {dati?.map((n) => {
          const dove = collegamentoNotifica(n)
          return (
            <tr key={n.id}>
              <td data-etichetta="Quando">{new Date(n.creata_il).toLocaleString('it-IT')}</td>
              <td data-etichetta="Notifica">
                <div className={n.letta_il ? 'titolo-notifica' : 'titolo-notifica titolo-notifica-nuova'}>
                  {n.titolo}
                </div>
                {n.corpo && <div className="piccolo tenue">{n.corpo}</div>}
                {dove && <div className="piccolo"><Link to={dove.a}>{dove.testo}</Link></div>}
              </td>
              <td data-etichetta="Stato">
                {n.letta_il ? <Etichetta tono="neutro">letta</Etichetta> : <Etichetta tono="accento">nuova</Etichetta>}
              </td>
            </tr>
          )
        })}
      </Guscio>
      <PreferenzeAvvisi />
      <TelefoniCollegati />
    </>
  )
}

type Telefono = { id: string; piattaforma: 'ios' | 'android'; nome: string | null; creato_il: string; ultimo_uso: string }

/**
 * Telefoni collegati alle notifiche push (484): ognuno vede e rimuove i propri. Si rimuove un
 * telefono perso, venduto o non piu' usato; chi esce dall'app lo toglie gia' da se'. Se il server
 * non sa mandare push e non c'e' nessun telefono, la sezione non compare: non ci sarebbe niente da
 * fare.
 */
function TelefoniCollegati() {
  const [dati, setDati] = useState<{ push_attiva: boolean; dispositivi: Telefono[] } | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState<string | null>(null)

  const carica = useCallback(() => {
    api.get<{ push_attiva: boolean; dispositivi: Telefono[] }>('/dispositivi')
      .then((r) => { setDati(r); setErrore(null) })
      .catch((e) => setErrore(messaggioErrore(e)))
  }, [])
  useEffect(carica, [carica])

  async function rimuovi(id: string) {
    setInCorso(id)
    try {
      await api.elimina(`/dispositivi/${id}`)
      carica()
    } catch (e) {
      setErrore(messaggioErrore(e))
    } finally {
      setInCorso(null)
    }
  }

  if (!dati && !errore) return null
  if (dati && !dati.push_attiva && !dati.dispositivi.length) return null
  const corrente = telefonoCorrente()

  return (
    <Riquadro titolo="Telefoni collegati">
      <p className="piccolo tenue">
        Sui telefoni con l’app CIDIS arriva un avviso quando qui c’è una notifica nuova. Sullo schermo
        compare solo «Hai un nuovo avviso nel gestionale»: per leggerla apri l’app. «Rimuovi» scollega un
        telefono perso o che non usi più; uscendo dall’app il telefono si scollega da solo.
      </p>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {dati && !dati.push_attiva && (
        <Avviso tipo="attenzione">Le notifiche sul telefono non sono attive: per ora non ne arrivano.</Avviso>
      )}
      {dati && (
        <Tabella intestazioni={['Telefono', 'Collegato il', 'Ultimo uso', '']}
                 vuoto={<Vuoto testo={inAppNativa()
                   ? 'Questo telefono non riceve le notifiche: consentile all’app CIDIS nelle impostazioni del telefono, poi esci e rientra.'
                   : 'Nessun telefono collegato. Installa l’app CIDIS ed entra con le tue credenziali: il telefono compare qui.'} />}>
          {dati.dispositivi.map((d) => (
            <tr key={d.id}>
              <td data-etichetta="Telefono">
                {d.nome ?? (d.piattaforma === 'ios' ? 'iPhone' : 'Telefono Android')}
                {d.id === corrente && <> <Etichetta tono="accento">questo telefono</Etichetta></>}
              </td>
              <td data-etichetta="Collegato il">{giorno(d.creato_il)}</td>
              <td data-etichetta="Ultimo uso">{new Date(d.ultimo_uso).toLocaleString('it-IT')}</td>
              <td data-etichetta="">
                <Bottone variante="discreto" dimensione="piccolo" inCorso={inCorso === d.id}
                         onClick={() => void rimuovi(d.id)}>
                  Rimuovi
                </Bottone>
              </td>
            </tr>
          ))}
        </Tabella>
      )}
    </Riquadro>
  )
}
